import {
  Breakpoints,
  createTheme,
  CSSObject,
  responsiveFontSizes,
  styled,
} from "@mui/material/styles";
import { colorOverrides as colors } from "theme/colorOverrides";
import {
  DOCS_ACCENT,
  DOCS_BORDER,
  DOCS_CONTENT_PX,
  DOCS_HEADING,
  DOCS_MUTED,
  DOCS_TEXT,
} from "../docsStyles";

// Desktop sizes. responsiveFontSizes scales these down toward 1rem on smaller
// screens (factor 2) and reaches the full size at md, this theme's 1023px breakpoint.
const DOCS_HEADING_MAX = {
  h1: "28px",
  h2: "22px",
  h3: "18px",
  h4: "16px",
} as const;

const fontSizeOnly = (style: CSSObject): CSSObject =>
  Object.fromEntries(
    Object.entries(style).filter(
      ([key]) => key === "fontSize" || key.startsWith("@media")
    )
  );

const docsHeadingFontSizes = (breakpoints: Breakpoints["values"]) => {
  const { typography } = responsiveFontSizes(
    createTheme({
      typography: {
        htmlFontSize: 16,
        h1: { fontSize: DOCS_HEADING_MAX.h1 },
        h2: { fontSize: DOCS_HEADING_MAX.h2 },
        h3: { fontSize: DOCS_HEADING_MAX.h3 },
        h4: { fontSize: DOCS_HEADING_MAX.h4 },
      },
      breakpoints: { values: breakpoints },
    }),
    {
      // Full size lands at md. The default also includes lg, which would leave md short of 28px.
      breakpoints: ["sm", "md"],
      variants: ["h1", "h2", "h3", "h4"],
      // Grid alignment can round the md size off the px value above.
      disableAlign: true,
    }
  );

  return {
    h1: fontSizeOnly(typography.h1),
    h2: fontSizeOnly(typography.h2),
    h3: fontSizeOnly(typography.h3),
    h4: fontSizeOnly(typography.h4),
  };
};

export const MarkdownContent = styled("div")(({ theme }) => {
  const heading = docsHeadingFontSizes(theme.breakpoints.values);

  return {
    // Takes the card's spare height, which pushes the pager to the bottom.
    flex: "1 0 auto",
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(DOCS_CONTENT_PX.xs),
    paddingRight: theme.spacing(DOCS_CONTENT_PX.xs),
    color: DOCS_TEXT,
    // Lexend is intentionally broad; a system UI stack keeps long-form docs readable while fitting
    // more words per line. This is scoped to the document so the rest of the app keeps its branding.
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    fontSize: 14,
    lineHeight: 1.55,
    // Long URLs and identifiers wrap rather than widening the page on a phone.
    overflowWrap: "anywhere",
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.spacing(DOCS_CONTENT_PX.sm),
      paddingRight: theme.spacing(DOCS_CONTENT_PX.sm),
    },
    [theme.breakpoints.up("md")]: {
      paddingTop: theme.spacing(5),
      paddingBottom: theme.spacing(5),
      paddingLeft: theme.spacing(DOCS_CONTENT_PX.md),
      paddingRight: theme.spacing(DOCS_CONTENT_PX.md),
      fontSize: 15,
    },
    "& > :first-child": { marginTop: 0 },
    "& h1, & h2, & h3, & h4": {
      color: DOCS_HEADING,
      fontWeight: 700,
      letterSpacing: "-0.01em",
    },
    "& h1": {
      ...heading.h1,
      lineHeight: 1.2,
      marginBottom: theme.spacing(3),
    },
    // The paragraph right under the title reads as the page's summary.
    "& h1 + p": {
      fontSize: "1rem",
      lineHeight: 1.5,
      color: DOCS_MUTED,
      marginTop: 0,
      [theme.breakpoints.up("md")]: { fontSize: "1.0625rem" },
    },
    "& h2": {
      ...heading.h2,
      lineHeight: 1.3,
      marginTop: theme.spacing(7),
      marginBottom: theme.spacing(2),
    },
    "& h3": {
      ...heading.h3,
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(2),
    },
    "& h4": {
      ...heading.h4,
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(2),
    },
    "& a": {
      color: DOCS_ACCENT,
      fontWeight: 500,
      textDecoration: "none",
      "&:hover": { textDecoration: "underline" },
    },
    "& code": {
      fontSize: "0.9em",
      backgroundColor: colors.brand14,
      border: `1px solid ${DOCS_BORDER}`,
      borderRadius: theme.shape.borderRadius,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    "& pre": {
      backgroundColor: colors.brand14,
      border: `1px solid ${DOCS_BORDER}`,
      padding: theme.spacing(4),
      borderRadius: Number(theme.shape.borderRadius) * 2,
      overflowX: "auto",
      overflowWrap: "normal",
      "& code": { border: 0, padding: 0, backgroundColor: "transparent" },
    },
    // A wide table scrolls inside its own box instead of the page scrolling sideways.
    "& table": {
      display: "block",
      maxWidth: "100%",
      overflowX: "auto",
      borderCollapse: "collapse",
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(5),
      fontSize: "0.95em",
    },
    "& th, & td": {
      borderBottom: `1px solid ${DOCS_BORDER}`,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      textAlign: "left",
      overflowWrap: "normal",
    },
    "& th": {
      color: DOCS_MUTED,
      fontWeight: 500,
      backgroundColor: colors.brand14,
    },
    "& img": {
      maxWidth: "100%",
      height: "auto",
      borderRadius: Number(theme.shape.borderRadius) * 2,
    },
  };
});
